import React, { useEffect, useRef, useState } from "react";

import { StatePanel, Tabs } from "../../components/ui";
import { explainNoHits } from "./buyTargetExplain.js";
import BuyTargetContext from "./BuyTargetContext.jsx";
import BuyTargetEditor from "./BuyTargetEditor.jsx";
import CoreListMatchView from "./CoreListMatchView.jsx";
import MarketplaceWatch from "./MarketplaceWatch.jsx";

/**
 * The expanded section for ONE Core List reference, as two tabs: Matches and
 * Set target.
 *
 * Matches comes FIRST and opens by default (Guillaume, 2026-10-12: "we need to
 * see the prices before we set the target"). It used to be disabled until a
 * target existed, which put the evidence behind the decision it informs. With
 * no target it lists every quote for the reference; once one is set it sorts
 * them into the at-target / year-unknown / not-in-target buckets.
 *
 * The Matches tab is only mounted while it is the open one, so a collapsed
 * card never polls for quotes nobody is looking at.
 *
 * Props:
 *   - itemId        core_list_item_id
 *   - entry         this reference's slice of /api/core-list/targets
 *   - onChanged()   refetch trigger after a save
 *   - initialTab    "matches" | "target"
 */
const BuyTargetPanel = ({ itemId, reference = "", entry, onChanged, initialTab = "matches" }) => {
  // MarketplaceWatch hands its save up here, so the single Save targets button
  // persists the price AND where to look in one click. A ref rather than
  // state: it is a function the child owns, not something this renders from.
  const saveSourcesRef = useRef(null);

  const [tab, setTab] = useState(initialTab);

  // Clicking the count on the photo of a reference whose panel is ALREADY open
  // has to move the tab, and the panel does not remount for that.
  useEffect(() => {
    setTab(initialTab);
  }, [initialTab, itemId]);

  const targets = entry?.targets || [];
  const hasTargets = targets.length > 0;

  // COUNTS, not rows — /api/core-list/targets stopped sending the match rows
  // when the match view got its own paginated endpoint.
  const hitCount = Number(entry?.hit_count) || 0;
  const quoteCount =
    hitCount + (Number(entry?.unknown_year_count) || 0) + (Number(entry?.above_count) || 0);

  const items = [
    // The hit count, not the total: it is the number the card promises and
    // the one worth money. The other two buckets are inside.
    { value: "matches", label: "Matches", count: hasTargets && hitCount ? hitCount : undefined },
    { value: "target", label: hasTargets ? "Buy target" : "Set target" },
  ];

  return (
    <div className="buy-target-panel">
      <Tabs
        variant="underline"
        items={items}
        value={tab}
        onChange={setTab}
        aria-label="Reference"
        className="buy-target-panel__tabs"
      />

      {tab === "matches" ? (
        <CoreListMatchView itemId={itemId} reference={reference} hasTargets={hasTargets} />
      ) : (
        <div className="buy-target-panel__grid">
          <section className="buy-target-panel__section" aria-labelledby={`bt-${itemId}-targets`}>
            <header className="buy-target-panel__head">
              <h3 className="buy-target-panel__title" id={`bt-${itemId}-targets`}>
                Buy targets
              </h3>
              <p className="buy-target-panel__hint">
                The most you would pay, per production year. A band covers its years and every
                later one.
              </p>
            </header>

            <BuyTargetEditor
              itemId={itemId}
              targets={targets}
              onSaved={onChanged}
              // Saved in the same click as the targets, after they land.
              alsoSave={saveSourcesRef}
              beforeActions={
                <MarketplaceWatch
                  itemId={itemId}
                  marketplace={entry?.marketplace}
                  hasTargets={hasTargets}
                  registerSave={(fn) => {
                    saveSourcesRef.current = fn;
                  }}
                />
              }
            />

            {/* The reason the Matches tab has nothing AT TARGET, said where
                the target is set. "Nothing at your price yet" is the sentence
                that exposed the unit mismatch; without it zero looks broken. */}
            {hasTargets && quoteCount === 0 && (
              <StatePanel
                state="empty"
                compact
                title="Nothing at your price yet"
                description={explainNoHits(entry?.diagnostics)}
              />
            )}
          </section>

          <section
            className="buy-target-panel__section buy-target-panel__section--context"
            aria-labelledby={`bt-${itemId}-context`}
          >
            <header className="buy-target-panel__head">
              <h3 className="buy-target-panel__title" id={`bt-${itemId}-context`}>
                What it trades at
              </h3>
              <p className="buy-target-panel__hint">What the ceiling is set against.</p>
            </header>
            <BuyTargetContext itemId={itemId} open />
          </section>
        </div>
      )}
    </div>
  );
};

export default BuyTargetPanel;
